/** Assistant AI mode: rules (default), local Ollama, or Gemini cloud. */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "evp_asst_ai_mode";
const MODE_EVENT = "evp-assistant-ai-mode";

export const AI_MODES = {
  RULES: "rules",
  LOCAL: "local",
  GEMINI: "gemini",
};

export function isGeminiOffered() {
  return process.env.REACT_APP_ASSISTANT_GEMINI_ENABLED === "true";
}

function readStoredMode() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === AI_MODES.RULES || raw === AI_MODES.LOCAL || raw === AI_MODES.GEMINI) {
    return raw;
  }
  // Migrate legacy toggles
  const route = localStorage.getItem("evp_asst_ai_route");
  const gemini = localStorage.getItem("evp_asst_gemini");
  if (route === "1" && gemini === "1") return AI_MODES.GEMINI;
  if (route === "1") return AI_MODES.LOCAL;
  return AI_MODES.RULES;
}

export function getAssistantAiMode() {
  return readStoredMode();
}

export function setAssistantAiMode(mode) {
  const next =
    mode === AI_MODES.LOCAL || mode === AI_MODES.GEMINI ? mode : AI_MODES.RULES;
  localStorage.setItem(STORAGE_KEY, next);
  // Keep legacy keys in sync for any external readers
  localStorage.setItem("evp_asst_ai_route", next === AI_MODES.RULES ? "0" : "1");
  localStorage.setItem("evp_asst_gemini", next === AI_MODES.GEMINI ? "1" : "0");
  window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: { mode: next } }));
  return next;
}

export function subscribeAssistantAiMode(listener) {
  const onChange = (event) => {
    listener(event?.detail?.mode ?? getAssistantAiMode());
  };
  window.addEventListener(MODE_EVENT, onChange);
  return () => window.removeEventListener(MODE_EVENT, onChange);
}

/** Derived flags for engine / LLM client. */
export function deriveAiFlags(mode, { llmBuildEnabled = true } = {}) {
  const aiRoutingEnabled = mode !== AI_MODES.RULES && llmBuildEnabled;
  const useGemini = mode === AI_MODES.GEMINI && isGeminiOffered() && llmBuildEnabled;
  return { aiRoutingEnabled, useGemini, mode };
}

export function useAssistantAiMode({ llmBuildEnabled = true } = {}) {
  const [mode, setModeState] = useState(getAssistantAiMode);

  useEffect(() => subscribeAssistantAiMode(setModeState), []);

  const setMode = useCallback((next) => {
    const resolved = setAssistantAiMode(next);
    setModeState(resolved);
  }, []);

  const { aiRoutingEnabled, useGemini } = deriveAiFlags(mode, { llmBuildEnabled });

  return {
    aiMode: mode,
    setAiMode: setMode,
    aiRoutingEnabled,
    useGemini,
  };
}
